import { Box, Checkbox, Table, TableBody, TableCell, TableHead, TableRow } from "@material-ui/core";
import { FC, memo, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { EnchancedSortLabel } from "components/tables/tableSortLabel";
import { useTypeSelectors } from "hooks/useTypeSelectors";
import { getVisibleFleetReportRows } from "hooks/useFleetReports";
import {
  fleetReportsSelectAllVans,
  fleetReportsSelectFixedCols,
  fleetReportsSetFilter,
} from "actions/fleetReportsAction";
import { FleetReportData } from "types/FleetReports";
import { FleetReportRow } from "./FleetReportRow";
import { getFleetReportsTableColumns } from "./tableData";
import { useStyles } from "./styles";

interface PropsType {
  isFixed?: boolean;
  page: number;
  rowsPerPage: number;
}

const FleetSelectAllCheckbox: FC<{ rows: FleetReportData[] }> = memo(({ rows }) => {
  const dispatch = useDispatch();
  const selectedIdList = useTypeSelectors((state) => state.fleetReports.selectedIdList);
  const selectableRows = rows.filter((row) => row.rate);
  const allSelected =
    selectableRows.length > 0 &&
    selectableRows.every((row) =>
      selectedIdList.some(
        (item) => item.van === row.van_number && item.month === row.month && item.year === row.year,
      ),
    );

  return (
    <Checkbox
      name="selectAll"
      checked={allSelected}
      onChange={() => dispatch(fleetReportsSelectAllVans(rows))}
      disabled={!selectableRows.length}
    />
  );
});

const FleetReportsTable: FC<PropsType> = ({ isFixed, page, rowsPerPage }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const fleetReportList = useTypeSelectors((state) => state.fleetReports.fleetReportList);
  const offBoardedFleetReportList = useTypeSelectors(
    (state) => state.fleetReports.offBoardedFleetReportList,
  );
  const fleetReportFilters = useTypeSelectors((state) => state.fleetReports.fleetReportFilters);
  const [sortId, setSortId] = useState<string>(fleetReportFilters?.ordering || "van_van_number");

  const columns = useMemo(
    () =>
      getFleetReportsTableColumns(
        fleetReportFilters?.activeColumns,
        isFixed,
        fleetReportFilters?.fixedColumns,
      ),
    [fleetReportFilters?.activeColumns, fleetReportFilters?.fixedColumns, isFixed],
  );
  const dataColumns = useMemo(() => columns.filter((col) => col.id !== "checkbox"), [columns]);

  const { offBoardedRows, actualRows } = useMemo(
    () => getVisibleFleetReportRows(fleetReportList, offBoardedFleetReportList, fleetReportFilters),
    [fleetReportList, offBoardedFleetReportList, fleetReportFilters],
  );

  const pagedRows = useMemo(() => {
    const allRows = [
      ...offBoardedRows.map((row) => ({ row, isOffBoarded: true })),
      ...actualRows.map((row) => ({ row, isOffBoarded: false })),
    ];
    return allRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [offBoardedRows, actualRows, page, rowsPerPage]);

  const visibleRows = useMemo(() => pagedRows.map((item) => item.row), [pagedRows]);

  const handleSortChange = (currentSort: string | undefined, cellId: string) => {
    if (currentSort?.replace("-", "") === cellId) {
      const sortString = `${currentSort?.includes("-") ? "" : "-"}${cellId}`;
      setSortId(sortString);
      dispatch(fleetReportsSetFilter({ ordering: sortString }));
    } else {
      setSortId(cellId);
      dispatch(fleetReportsSetFilter({ ordering: cellId }));
    }
  };

  return (
    <Table stickyHeader>
      <TableHead>
        <TableRow>
          {columns.map((col, index) => (
            <TableCell key={col.id} className={classes.tableHeadCell}>
              <Box className={`${classes.tableCellContent} ${classes.borderCellRightLight}`}>
                {col.id === "checkbox" ? (
                  <FleetSelectAllCheckbox rows={visibleRows} />
                ) : (
                  <>
                    <Checkbox
                      name={`${col.id}`}
                      checked={fleetReportFilters?.fixedColumns?.some((item) => item === col.id)}
                      onChange={() => dispatch(fleetReportsSelectFixedCols(col, index))}
                      disabled={
                        fleetReportFilters?.fixedColumns?.length === 4 &&
                        !fleetReportFilters?.fixedColumns?.some((item) => item === col.id)
                      }
                    />
                    <EnchancedSortLabel
                      columnId={col.id}
                      columnLabel={col.label}
                      hasSortLabel={Boolean(col?.hasSort)}
                      handleChange={handleSortChange}
                      ordering={sortId}
                    />
                  </>
                )}
              </Box>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {pagedRows.map(({ row, isOffBoarded }) => (
          <FleetReportRow
            key={`${isOffBoarded ? "offboarded-" : ""}${row.van}-${row.month}-${row.year}`}
            row={row}
            isOffBoarded={isOffBoarded}
            isFixed={isFixed}
            columns={dataColumns}
            classes={classes}
          />
        ))}
      </TableBody>
    </Table>
  );
};

export default FleetReportsTable;
